import * as preact from "preact";
import * as _ from "underscore";

import * as deviceDetect from "deviceDetect";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import * as sharedConfig from "shared/sharedConfig";
import { cn } from "shared/utils";

interface Props {}

interface Stats {
  date: string;
  wordsRead: number;
  wordsPracticed: number;
  streakRepair: number;
  byLanguage?: {
    [languageCode: string]: {
      wordsRead: number;
      wordsPracticed: number;
    };
  };
}

type ViewMode = "combined" | "wordsRead" | "wordsPracticed";

type State =
  | {
      type: "fetching";
    }
  | {
      type: "fetched";
      years: {
        year: number;
        days: Stats[]; // stats for each day of the year, either 365 or 366
      }[];
      isSmallScreen: boolean; // in this case we need to adjust the display
      viewMode: ViewMode;
      selectedLanguage: string; // "all" or a specific language code
      availableLanguages: string[]; // language codes that have data
    };

class HeatMapProgressView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      type: "fetching",
    };

    this.fetchData();
  }

  async fetchData() {
    /** The date key is in the format `YYYY-MM-DD` */
    const rawStatsList: Stats[] = (
      await readlangClient.request(
        "GET",
        "/api/userLearningLog/allTimeStats",
        {}
      )
    ).data.allTimeStats;

    // Extract available languages
    const languageSet = new Set<string>();
    for (const stats of rawStatsList) {
      if (stats.byLanguage) {
        for (const lang of Object.keys(stats.byLanguage)) {
          languageSet.add(lang);
        }
      }
    }
    const availableLanguages = Array.from(languageSet).sort();

    const dateToStats: { [date: string]: Stats } = _.indexBy(
      rawStatsList,
      "date"
    );

    const years: { year: number; days: Stats[] }[] = (() => {
      // Helper to check leap year
      function isLeapYear(year: number) {
        return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
      }

      // Collect all years present in the data
      const yearSet = new Set<number>();
      for (const stats of rawStatsList) {
        const year = parseInt(stats.date.slice(0, 4), 10);
        yearSet.add(year);
      }
      const yearsArr = Array.from(yearSet).sort((a, b) => a - b);

      // For each year, build the days array
      return yearsArr.map((year) => {
        const daysInYear = isLeapYear(year) ? 366 : 365;
        const days: Stats[] = [];
        for (let day = 0; day < daysInYear; day++) {
          const date = new Date(Date.UTC(year, 0, 1 + day));
          const dateStr = date.toISOString().slice(0, 10);
          days.push(
            dateToStats[dateStr] ?? {
              date: dateStr,
              wordsRead: 0,
              wordsPracticed: 0,
              streakRepair: 0,
            }
          );
        }
        return { year, days };
      });
    })();

    this.setState({
      type: "fetched",
      years,
      isSmallScreen: deviceDetect.isSmall(),
      viewMode: "combined",
      selectedLanguage: "all",
      availableLanguages,
    });
  }

  render() {
    if (this.state.type === "fetching") {
      return <div>Loading...</div>;
    }

    // Color intensity constants
    const colorNone = "none";
    const colorLow = "low";
    const colorMid = "mid";
    const colorHigh = "high";
    const colorMax = "max";

    // Responsive cell size is handled by CSS
    function getColorClass(
      intensity: number,
      max: number,
      streakRepair: number
    ) {
      if (streakRepair > 0) return "streakRepair";
      if (intensity === 0) return colorNone;
      if (intensity < max * 0.1) return colorLow;
      if (intensity < max * 0.3) return colorMid;
      if (intensity < max * 0.5) return colorHigh;
      return colorMax;
    }

    // Helper function to get intensity based on view mode and selected language
    const getIntensity = (day: Stats, viewMode: ViewMode): number => {
      if (this.state.type !== "fetched") return 0;

      const selectedLang = this.state.selectedLanguage;

      // If a specific language is selected
      if (selectedLang !== "all") {
        const langData = day.byLanguage?.[selectedLang] ?? {
          wordsRead: 0,
          wordsPracticed: 0,
        };
        switch (viewMode) {
          case "wordsRead":
            return langData.wordsRead;
          case "wordsPracticed":
            return langData.wordsPracticed;
          case "combined":
          default:
            return (
              langData.wordsRead +
              sharedConfig.wordEquivalentOfPractice * langData.wordsPracticed
            );
        }
      }

      // Otherwise use the totals
      switch (viewMode) {
        case "wordsRead":
          return day.wordsRead;
        case "wordsPracticed":
          return day.wordsPracticed;
        case "combined":
        default:
          return (
            day.wordsRead +
            sharedConfig.wordEquivalentOfPractice * day.wordsPracticed
          );
      }
    };

    // Find max intensity for scaling based on current view mode
    const currentViewMode = this.state.viewMode;
    const allIntensities = _.flatten(
      this.state.years.map((y) =>
        y.days.map((d) => getIntensity(d, currentViewMode))
      )
    );
    const maxIntensity = Math.max(1, ...allIntensities);

    const today = new Date().toISOString().slice(0, 10);

    return (
      <div class="heatmapProgressView">
        <h2 class="pageTitle">Long Term Progress</h2>

        <div class="heatmapViewControls">
          {this.state.availableLanguages.length > 1 && (
            <div class="languageSelector">
              {/* <label for="languageSelect">Language: </label> */}
              <select
                id="languageSelect"
                value={this.state.selectedLanguage}
                onChange={(e) =>
                  this.handleLanguageChange(
                    (e.target as HTMLSelectElement).value
                  )
                }
              >
                <option value="all">All Languages</option>
                {this.state.availableLanguages.map((lang) => (
                  <option key={lang} value={lang}>
                    {languageMap.map[lang].english}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div class="viewModeToggle">
            <button
              class={`toggleButton ${this.state.viewMode === "combined" ? "active" : ""}`}
              onClick={() => this.handleViewModeChange("combined")}
            >
              Combined
            </button>
            <button
              class={`toggleButton ${this.state.viewMode === "wordsRead" ? "active" : ""}`}
              onClick={() => this.handleViewModeChange("wordsRead")}
            >
              Words Read
            </button>
            <button
              class={`toggleButton ${this.state.viewMode === "wordsPracticed" ? "active" : ""}`}
              onClick={() => this.handleViewModeChange("wordsPracticed")}
            >
              Words Practiced
            </button>
          </div>
        </div>

        <div class="heatmapYears">
          {this.state.years.map((yearObj) => {
            if (this.state.type !== "fetched") {
              throw new Error("State is not fetched");
            }

            // Group days into weeks (start on Sunday)
            const weeks: Stats[][] = [];
            let week: Stats[] = [];
            const firstDay = new Date(yearObj.days[0].date).getUTCDay();
            // Pad first week
            for (let i = 0; i < firstDay; i++) {
              week.push(undefined as any);
            }
            yearObj.days.forEach((day) => {
              week.push(day);
              if (week.length === 7) {
                weeks.push(week);
                week = [];
              }
            });
            if (week.length) {
              while (week.length < 7) week.push(undefined as any);
              weeks.push(week);
            }

            const rows = this.state.isSmallScreen
              ? [
                  weeks.slice(0, weeks.length / 2 + 1),
                  weeks.slice(weeks.length / 2 + 1),
                ]
              : [weeks];

            return (
              <div key={yearObj.year}>
                <h3 class="heatmapYear">{yearObj.year}</h3>
                <div class="heatmapGrid">
                  {rows.map((rowWeeks) => {
                    return (
                      <div class="heatmapGridRow">
                        {rowWeeks.map((weekDays, weekIdx) => (
                          <div key={weekIdx} class="heatmapWeek">
                            {weekDays.map((day, dayIdx) => (
                              <div
                                key={dayIdx}
                                data-tooltip={
                                  day
                                    ? `${day.date}|${day.wordsRead}|${day.wordsPracticed}`
                                    : ""
                                }
                                class={
                                  "heatmapDay " +
                                  (day
                                    ? day.date > today
                                      ? "filler"
                                      : getColorClass(
                                          getIntensity(day, currentViewMode),
                                          maxIntensity,
                                          day.streakRepair
                                        )
                                    : "filler")
                                }
                              >
                                {day ? (
                                  <div
                                    class={cn({
                                      heatmapDayTooltip: true,
                                      startOfRow: weekIdx < 6,
                                      endOfRow: weekIdx > rowWeeks.length - 7,
                                    })}
                                  >
                                    <h3>{this.dateString(day.date)}</h3>
                                    <ul>
                                      <li>
                                        Words Read:{" "}
                                        {this.getDisplayValue(day, "wordsRead")}
                                      </li>
                                      <li>
                                        Words Practiced:{" "}
                                        {this.getDisplayValue(
                                          day,
                                          "wordsPracticed"
                                        )}
                                      </li>
                                      {this.getDisplayValue(
                                        day,
                                        "streakRepair"
                                      ) > 0 ? (
                                        <li>
                                          <strong>Streak Repaired</strong>
                                        </li>
                                      ) : null}
                                    </ul>
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <p>
          {this.state.viewMode === "combined" &&
            `This shows your combined reading and practice activity ${
              this.state.selectedLanguage === "all"
                ? "across all languages"
                : `in ${this.state.selectedLanguage.toUpperCase()}`
            } on Readlang.`}
          {this.state.viewMode === "wordsRead" &&
            `This shows your reading activity ${
              this.state.selectedLanguage === "all"
                ? "across all languages"
                : `in ${this.state.selectedLanguage.toUpperCase()}`
            } on Readlang.`}
          {this.state.viewMode === "wordsPracticed" &&
            `This shows your practice activity ${
              this.state.selectedLanguage === "all"
                ? "across all languages"
                : `in ${this.state.selectedLanguage.toUpperCase()}`
            } on Readlang.`}
        </p>
        {this.state.years.map((year) => year.year).includes(2023) ? (
          <p>
            Note that the data is only available from the end of April 2023
            onwards.
          </p>
        ) : null}
      </div>
    );
  }

  dateString(date: string) {
    const [year, month, day] = date.split("-").map(Number);

    return `${day} ${dateUtils.months[month - 1]} ${year}`;
  }

  getDisplayValue(
    day: Stats,
    field: "wordsRead" | "wordsPracticed" | "streakRepair"
  ): number {
    if (this.state.type !== "fetched") return 0;

    const selectedLang = this.state.selectedLanguage;

    // If a specific language is selected and byLanguage data exists
    if (selectedLang !== "all") {
      return day.byLanguage?.[selectedLang]?.[field] ?? 0;
    }

    // Otherwise use the totals
    return day[field];
  }

  handleResize = () => {
    if (this.state.type === "fetched") {
      this.setState({
        ...this.state,
        isSmallScreen: deviceDetect.isSmall(),
      });
    }
  };

  handleViewModeChange = (viewMode: ViewMode) => {
    if (this.state.type === "fetched") {
      this.setState({
        ...this.state,
        viewMode,
      });
    }
  };

  handleLanguageChange = (language: string) => {
    if (this.state.type === "fetched") {
      this.setState({
        ...this.state,
        selectedLanguage: language,
      });
    }
  };

  componentDidMount(): void {
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount(): void {
    window.removeEventListener("resize", this.handleResize);
  }
}

export default HeatMapProgressView;
